/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: 2.22.0
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Money } from './Money';
import {
    MoneyFromJSON,
    MoneyFromJSONTyped,
    MoneyToJSON,
} from './Money';
import type { ExternalStatusEnum } from './ExternalStatusEnum';
import {
    ExternalStatusEnumFromJSON,
    ExternalStatusEnumFromJSONTyped,
    ExternalStatusEnumToJSON,
} from './ExternalStatusEnum';
import type { InvestmentCancellationEnum } from './InvestmentCancellationEnum';
import {
    InvestmentCancellationEnumFromJSON,
    InvestmentCancellationEnumFromJSONTyped,
    InvestmentCancellationEnumToJSON,
} from './InvestmentCancellationEnum';
import type { SurveyWaiverTypeEnum } from './SurveyWaiverTypeEnum';
import {
    SurveyWaiverTypeEnumFromJSON,
    SurveyWaiverTypeEnumFromJSONTyped,
    SurveyWaiverTypeEnumToJSON,
} from './SurveyWaiverTypeEnum';
import type { PaymentDetails } from './PaymentDetails';
import {
    PaymentDetailsFromJSON,
    PaymentDetailsFromJSONTyped,
    PaymentDetailsToJSON,
} from './PaymentDetails';
import type { QuestionnaireQuestion } from './QuestionnaireQuestion';
import {
    QuestionnaireQuestionFromJSON,
    QuestionnaireQuestionFromJSONTyped,
    QuestionnaireQuestionToJSON,
} from './QuestionnaireQuestion';

/**
 * 
 * @export
 * @interface ExternalInvestment
 */
export interface ExternalInvestment {
    /**
     * 
     * @type {string}
     * @memberof ExternalInvestment
     */
    readonly id: string;
    /**
     * 
     * @type {string}
     * @memberof ExternalInvestment
     */
    invitationId?: string;
    /**
     * 
     * @type {string}
     * @memberof ExternalInvestment
     */
    campaignId?: string;
    /**
     * 
     * @type {string}
     * @memberof ExternalInvestment
     */
    investorId?: string;
    /**
     * The number of units the investor is purchasing. (Distributor API)
     * @type {number}
     * @memberof ExternalInvestment
     */
    units?: number;
    /**
     * The disagio reduces the payable amount but not the nominal amount.
     * @type {Money}
     * @memberof ExternalInvestment
     */
    disagio?: Money;
    /**
     * Accrued interests, if applicable, to the given point in time (in case of offer) or at the time of acceptance (contract).
     * @type {Money}
     * @memberof ExternalInvestment
     */
    readonly accruedInterest: Money;
    /**
     * Total invested amount, in EUR
     * @type {Money}
     * @memberof ExternalInvestment
     */
    readonly investmentAmount: Money;
    /**
     * Total amount to pay, in EUR (investment amount minus potential discounts)
     * @type {Money}
     * @memberof ExternalInvestment
     */
    readonly paymentTotal: Money;
    /**
     * Exemption order amount the investor wants to allocate for this investment.
     * @type {Money}
     * @memberof ExternalInvestment
     */
    exemptionOrderAmount?: Money | null;
    /**
     * the amount of tokens the investor has remaining on this investment
     * @type {number}
     * @memberof ExternalInvestment
     */
    readonly remainingAmount: number;
    /**
     * The date where the investment has been signed.
     * @type {Date}
     * @memberof ExternalInvestment
     */
    signedAt?: Date;
    /**
     * The date on which the issuer accepted the investment offer. If not set the investment will be shown in the Cashlink Studio for approval.
     * @type {Date}
     * @memberof ExternalInvestment
     */
    acceptedAt?: Date;
    /**
     * The date on which the investor paid the payable amount.
     * @type {Date}
     * @memberof ExternalInvestment
     */
    paidAt?: Date;
    /**
     * The date on which the digital assets have been credited to the investor's wallet.
     * @type {Date}
     * @memberof ExternalInvestment
     */
    readonly deliveredAt: Date | null;
    /**
     * The date on which the the investment was canceled. The reason is given in cancellation_reason. If set, the process cannot be finished.
     * @type {Date}
     * @memberof ExternalInvestment
     */
    readonly cancellationDate: Date;
    /**
     * 
     * Cancelation reason:
     * ABORTION: The investment has been canceled by the investor.
     * REJECTION: The investment has been rejected by the issuer.
     * KYC_INSUFFICIENT: The investment has been canceled as identification or kyc is missing.
     * PAYMENT_MISSING: The investment has been canceled as payment is missing or too late.
     * CANCELLATION: The investment has been canceled on investor request after acceptance.
     * 
     * @type {InvestmentCancellationEnum}
     * @memberof ExternalInvestment
     */
    readonly cancellationReason: InvestmentCancellationEnum;
    /**
     * The id of the wallet created for the investor. The wallet id is an optional parameter. It can be omitted or added later via PATCH. If a wallet id is required the status will change to DELIVERED only after a wallet id has been set and assets have been issued. Used for tokenized products only.
     * @type {string}
     * @memberof ExternalInvestment
     */
    wallet?: string;
    /**
     * Returns self-disclosure monthly income if required by product (Investment-as-a-Service API) or None (Distributor API)
     * @type {boolean}
     * @memberof ExternalInvestment
     */
    readonly selfDisclosureMonthlyIncome: boolean;
    /**
     * Returns self-disclosure net worth if required by product (Investment-as-a-Service API) or None (Distributor API)
     * @type {boolean}
     * @memberof ExternalInvestment
     */
    readonly selfDisclosureNetWorth100k: boolean;
    /**
     * Returns self-disclosure submitted date if required by product (Investment-as-a-Service API) or None (Distributor API)
     * @type {Date}
     * @memberof ExternalInvestment
     */
    readonly selfDisclosureSubmittedAt: Date;
    /**
     * returns survey answers (Investment-as-a-Service API) or None (Distributor API)[{"id": "investmentexperience_investment_fund", "selected_option": "no_answer"}]. POSSIBLE IDS: investmentexperience_investment_fund, investmentonlyexperience_investment_fund, investmentexperience_money_market_funds, investmentonlyexperience_money_market_funds, investmentexperience_share, investmentonlyexperience_share, investmentexperience_participation_right, investmentonlyexperience_participation_right, investmentexperience_crypto_currency, investmentonlyexperience_crypto_currency, investmentexperience_crowdfunding_startup_financing, investmentexperience_crowdfunding_expansion_financing, investmentexperience_crowdfunding_lending, investmentonlyexperience_crowdfunding_startup_financing, investmentonlyexperience_crowdfunding_expansion_financing, investmentonlyexperience_crowdfunding_lending, investmentexperience_unsecured_junior_debt, investmentonlyexperience_unsecured_junior_debt, investmentexperience_bond, investmentonlyexperience_bond, investmentexperience_participation_paper, investmentonlyexperience_participation_paper, investmentexperience_operative_participation_in_a_limited_partnership, investmentonlyexperience_operative_participation_in_a_limited_partnership, investmentexperience_contract_for_difference, investmentonlyexperience_contract_for_difference, investmentexperience_certificate, investmentonlyexperience_certificate, investmentexperience_warrants, investmentonlyexperience_warrants, investmentexperience_alternative_investment_fund, investmentonlyexperience_alternative_investment_fund, investmentknowledge_investment_fund, investmentknowledge_money_market_funds, investmentknowledge_bond, investmentknowledge_share, investmentknowledge_participation_right, investmentknowledge_participation_paper, investmentknowledge_operative_participation_in_a_limited_partnership, investmentknowledge_contract_for_difference, investmentknowledge_crypto_currency, investmentknowledge_crowdfunding_startup_financing, investmentknowledge_crowdfunding_expansion_financing, investmentknowledge_crowdfunding_lending, investmentknowledge_unsecured_junior_debt, investmentknowledge_certificate, investmentknowledge_warrants, investmentknowledge_alternative_investment_fund, serviceexperience_independent, serviceexperience_investment_consultancy, serviceexperience_investment_management, serviceexperience_crowdfunding_platforms, avgyearlytransactions, avgtransactionvalue, professionalinvestment_crowdfunding_platforms, professionalinvestment_crowdfunding_projects_or_companies, investmentdurationobjective_holdperiod, investmentsustainabilityobjective, investmentobjectives_general_capital_formation, investmentobjectives_speculation_startup_expansion_financing, investmentobjectives_general_pension_provision, investmentobjectives_diversification OPTIONS: no_answer, no_experience, no_knowledge_or_experience, knowledge, no_knowledge, only_knowledge, zero_to_three_years, three_to_five_years, greater_than_five_years, no_trans, lower_5_trans, lower_10_trans, more_than_10_trans, lower_3000, between_3000_and_5000, between_5000_and_10000, over_10000, apprentice, employee, leading_position, self_employed, short_term, mid_term, long_term.
     * @type {Array<QuestionnaireQuestion>}
     * @memberof ExternalInvestment
     */
    readonly survey: Array<QuestionnaireQuestion>;
    /**
     * returns survey waiver (Investment-as-a-Service API) or None (Distributor API). Possible values: NO_ANSWERS, UNSUITABLE
     * @type {SurveyWaiverTypeEnum}
     * @memberof ExternalInvestment
     */
    readonly surveyWaiver: SurveyWaiverTypeEnum;
    /**
     * returns a date (Investment-as-a-Service API) or None (Distributor API)
     * @type {Date}
     * @memberof ExternalInvestment
     */
    readonly surveySubmittedAt: Date;
    /**
     * Payment code and bank account information.
     * @type {PaymentDetails}
     * @memberof ExternalInvestment
     */
    readonly paymentDetails: PaymentDetails | null;
    /**
     * Transaction ID of the issuance
     * @type {string}
     * @memberof ExternalInvestment
     */
    readonly issuanceTransactionHash: string | null;
    /**
     * 
     * PENDING: Investment is not yet signed by investor.
     * KYC_PENDING: The identification process for the investor is still pending.
     * WAITING_ACCEPTANCE: The investment is ready for approval by the issuer.
     * ACCEPTED: The investment has been accepted by the issuer.
     * PAID: The investment amount has been marked as paid.
     * DELIVERED: The digital assets have been credited to the investor's wallet.
     * KYC_INVALID: The KYC/AML check could not be completed successfully due to missing or inconsistent data.
     * ABORTION: The investment has been canceled by the investor.
     * REJECTION: The investment has been rejected by the issuer.
     * KYC_INSUFFICIENT: The investment has been canceled as identification or kyc is missing.
     * PAYMENT_MISSING: The investment has been canceled as payment is missing or too late.
     * CANCELLATION: The investment has been canceled on investor request after acceptance.
     * 
     * @type {ExternalStatusEnum}
     * @memberof ExternalInvestment
     */
    readonly status: ExternalStatusEnum;
}



/**
 * Check if a given object implements the ExternalInvestment interface.
 */
export function instanceOfExternalInvestment(value: object): value is ExternalInvestment {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('accruedInterest' in value) || value['accruedInterest'] === undefined) return false;
    if (!('investmentAmount' in value) || value['investmentAmount'] === undefined) return false;
    if (!('paymentTotal' in value) || value['paymentTotal'] === undefined) return false;
    if (!('remainingAmount' in value) || value['remainingAmount'] === undefined) return false;
    if (!('deliveredAt' in value) || value['deliveredAt'] === undefined) return false;
    if (!('cancellationDate' in value) || value['cancellationDate'] === undefined) return false;
    if (!('cancellationReason' in value) || value['cancellationReason'] === undefined) return false;
    if (!('selfDisclosureMonthlyIncome' in value) || value['selfDisclosureMonthlyIncome'] === undefined) return false;
    if (!('selfDisclosureNetWorth100k' in value) || value['selfDisclosureNetWorth100k'] === undefined) return false;
    if (!('selfDisclosureSubmittedAt' in value) || value['selfDisclosureSubmittedAt'] === undefined) return false;
    if (!('survey' in value) || value['survey'] === undefined) return false;
    if (!('surveyWaiver' in value) || value['surveyWaiver'] === undefined) return false;
    if (!('surveySubmittedAt' in value) || value['surveySubmittedAt'] === undefined) return false;
    if (!('paymentDetails' in value) || value['paymentDetails'] === undefined) return false;
    if (!('issuanceTransactionHash' in value) || value['issuanceTransactionHash'] === undefined) return false;
    if (!('status' in value) || value['status'] === undefined) return false;
    return true;
}

export function ExternalInvestmentFromJSON(json: any): ExternalInvestment {
    return ExternalInvestmentFromJSONTyped(json, false);
}

export function ExternalInvestmentFromJSONTyped(json: any, ignoreDiscriminator: boolean): ExternalInvestment {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'invitationId': json['invitation_id'] == null ? undefined : json['invitation_id'],
        'campaignId': json['campaign_id'] == null ? undefined : json['campaign_id'],
        'investorId': json['investor_id'] == null ? undefined : json['investor_id'],
        'units': json['units'] == null ? undefined : json['units'],
        'disagio': json['disagio'] == null ? undefined : MoneyFromJSON(json['disagio']),
        'accruedInterest': MoneyFromJSON(json['accrued_interest']),
        'investmentAmount': MoneyFromJSON(json['investment_amount']),
        'paymentTotal': MoneyFromJSON(json['payment_total']),
        'exemptionOrderAmount': json['exemption_order_amount'] == null ? undefined : MoneyFromJSON(json['exemption_order_amount']),
        'remainingAmount': json['remaining_amount'],
        'signedAt': json['signed_at'] == null ? undefined : (new Date(json['signed_at'])),
        'acceptedAt': json['accepted_at'] == null ? undefined : (new Date(json['accepted_at'])),
        'paidAt': json['paid_at'] == null ? undefined : (new Date(json['paid_at'])),
        'deliveredAt': (json['delivered_at'] == null ? null : new Date(json['delivered_at'])),
        'cancellationDate': (new Date(json['cancellation_date'])),
        'cancellationReason': InvestmentCancellationEnumFromJSON(json['cancellation_reason']),
        'wallet': json['wallet'] == null ? undefined : json['wallet'],
        'selfDisclosureMonthlyIncome': json['self_disclosure_monthly_income'],
        'selfDisclosureNetWorth100k': json['self_disclosure_net_worth_100k'],
        'selfDisclosureSubmittedAt': (new Date(json['self_disclosure_submitted_at'])),
        'survey': ((json['survey'] as Array<any>).map(QuestionnaireQuestionFromJSON)),
        'surveyWaiver': SurveyWaiverTypeEnumFromJSON(json['survey_waiver']),
        'surveySubmittedAt': (new Date(json['survey_submitted_at'])),
        'paymentDetails': PaymentDetailsFromJSON(json['payment_details']),
        'issuanceTransactionHash': json['issuance_transaction_hash'],
        'status': ExternalStatusEnumFromJSON(json['status']),
    };
}

export function ExternalInvestmentToJSON(value?: Omit<ExternalInvestment, 'id'|'accrued_interest'|'investment_amount'|'payment_total'|'remaining_amount'|'delivered_at'|'cancellation_date'|'cancellation_reason'|'self_disclosure_monthly_income'|'self_disclosure_net_worth_100k'|'self_disclosure_submitted_at'|'survey'|'survey_waiver'|'survey_submitted_at'|'payment_details'|'issuance_transaction_hash'|'status'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'invitation_id': value['invitationId'],
        'campaign_id': value['campaignId'],
        'investor_id': value['investorId'],
        'units': value['units'],
        'disagio': MoneyToJSON(value['disagio']),
        'exemption_order_amount': MoneyToJSON(value['exemptionOrderAmount']),
        'signed_at': value['signedAt'] == null ? undefined : ((value['signedAt']).toISOString()),
        'accepted_at': value['acceptedAt'] == null ? undefined : ((value['acceptedAt']).toISOString()),
        'paid_at': value['paidAt'] == null ? undefined : ((value['paidAt']).toISOString()),
        'wallet': value['wallet'],
    };
}

